/**
 * siteMapViewportMemory — per-site map pan/zoom/type preference.
 *
 * Remembers the operator's last map viewport on the Site Map tab so
 * revisiting the site (or refreshing the page) restores the same frame
 * instead of re-running fitBounds every time.
 *
 * Scope: localStorage (UX preference, no audit value, survives reload).
 * Failures (private mode, quota) are swallowed — losing memory is fine.
 */

export interface SavedSiteMapViewport {
  center: { lat: number; lng: number };
  zoom: number;
  mapTypeId?: string;
}

const KEY_PREFIX = 'siterag:siteMapViewport:';

function isFiniteCoord(n: unknown): n is number {
  return typeof n === 'number' && Number.isFinite(n);
}

function parseViewport(raw: string | null): SavedSiteMapViewport | null {
  if (!raw) return null;
  try {
    const data = JSON.parse(raw) as Partial<SavedSiteMapViewport>;
    if (!data.center || !isFiniteCoord(data.center.lat) || !isFiniteCoord(data.center.lng)) {
      return null;
    }
    if (!isFiniteCoord(data.zoom) || data.zoom < 0 || data.zoom > 24) return null;
    const mapTypeId = typeof data.mapTypeId === 'string' && data.mapTypeId.length > 0
      ? data.mapTypeId
      : undefined;
    return {
      center: { lat: data.center.lat, lng: data.center.lng },
      zoom: data.zoom,
      mapTypeId,
    };
  } catch {
    return null;
  }
}

export function readLastSiteMapViewport(siteId: string): SavedSiteMapViewport | null {
  if (!siteId) return null;
  try {
    return parseViewport(window.localStorage.getItem(KEY_PREFIX + siteId));
  } catch {
    return null;
  }
}

export function writeLastSiteMapViewport(siteId: string, viewport: SavedSiteMapViewport): void {
  if (!siteId) return;
  if (!isFiniteCoord(viewport.center.lat) || !isFiniteCoord(viewport.center.lng)) return;
  if (!isFiniteCoord(viewport.zoom)) return;
  try {
    window.localStorage.setItem(KEY_PREFIX + siteId, JSON.stringify({
      center: viewport.center,
      zoom: viewport.zoom,
      ...(viewport.mapTypeId ? { mapTypeId: viewport.mapTypeId } : {}),
    }));
  } catch {
    /* quota / private mode */
  }
}

/** Test hook — remove all saved map viewports. */
export function clearSiteMapViewportMemoryForTests(): void {
  if (typeof window === 'undefined') return;
  try {
    for (let i = window.localStorage.length - 1; i >= 0; i -= 1) {
      const k = window.localStorage.key(i);
      if (k?.startsWith(KEY_PREFIX)) window.localStorage.removeItem(k);
    }
  } catch {
    /* swallow */
  }
}
